import {
  clampRateOfChange,
  DEFAULT_SMOOTHING_CONFIG,
  exponentialMovingAverage,
  SmoothingConfig,
} from '../utils/smoothing';
import { msToKmh } from '../utils/speed';

export class SpeedProcessor {
  private config: SmoothingConfig;
  private currentSmoothedSpeedKmh: number = 0;
  private lastUpdateTime: number = 0;

  constructor(config: Partial<SmoothingConfig> = {}) {
    this.config = { ...DEFAULT_SMOOTHING_CONFIG, ...config };
  }

  /**
   * Process raw GPS speed (in meters/second or already in km/h)
   * @param rawSpeed Speed value from GPS or input
   * @param isAlreadyKmh True if input is already in km/h, false if m/s
   * @param timestamp Optional timestamp in ms; defaults to performance.now()
   */
  public processSpeed(
    rawSpeed: number | null | undefined,
    isAlreadyKmh: boolean = false,
    timestamp: number = performance.now()
  ): number {
    // 1. Null / undefined / negative handling
    if (rawSpeed === null || rawSpeed === undefined || isNaN(rawSpeed) || rawSpeed < 0) {
      // If we received invalid data, maintain previous speed or slowly decay
      return this.currentSmoothedSpeedKmh;
    }

    const inputKmh = isAlreadyKmh ? rawSpeed : msToKmh(rawSpeed);

    // Calculate delta time
    const dtSeconds = this.lastUpdateTime > 0
      ? Math.max(0.01, (timestamp - this.lastUpdateTime) / 1000)
      : 0.1;
    this.lastUpdateTime = timestamp;

    // 2. Stationary filter / deadband
    if (inputKmh < this.config.stationaryThresholdKmh) {
      // Smoothly decay to zero
      this.currentSmoothedSpeedKmh = exponentialMovingAverage(
        0,
        this.currentSmoothedSpeedKmh,
        this.config.alpha
      );
      if (this.currentSmoothedSpeedKmh < 0.1) {
        this.currentSmoothedSpeedKmh = 0;
      }
      return this.currentSmoothedSpeedKmh;
    }

    // 3. Rate-of-change clamp (reject sudden GPS jumps like 20 -> 120 km/h in 0.5s)
    const rateClampedSpeed = clampRateOfChange(
      inputKmh,
      this.currentSmoothedSpeedKmh,
      dtSeconds,
      this.config.maxRateOfChangeKmhPerSec
    );

    // 4. Exponential Moving Average (EMA)
    this.currentSmoothedSpeedKmh = exponentialMovingAverage(
      rateClampedSpeed,
      this.currentSmoothedSpeedKmh,
      this.config.alpha
    );

    return this.currentSmoothedSpeedKmh;
  }

  public getSpeed(): number {
    return this.currentSmoothedSpeedKmh;
  }

  public reset(speed: number = 0): void {
    this.currentSmoothedSpeedKmh = Math.max(0, speed);
    this.lastUpdateTime = performance.now();
  }

  public setConfig(config: Partial<SmoothingConfig>): void {
    this.config = { ...this.config, ...config };
  }
}
